import { z } from "zod";

// ─── Challenge Category Constants ────────────────────────────────────────────

// Strict const tuple used for z.enum — must be kept in sync with CHALLENGE_CATEGORIES
export const CHALLENGE_CATEGORY_VALUES = [
  "Core Business Foundations",
  "Product & Technology",
  "Growth & Go-To-Market",
  "Fundraising & Investor Readiness",
  "Operations & Execution",
  "Financial & Business Health",
  "Legal & Compliance",
  "Retention & Experience",
  "Other",
] as const;

export type ChallengeCategoryValue = (typeof CHALLENGE_CATEGORY_VALUES)[number];

// Full objects used for tile rendering in the UI
export const CHALLENGE_CATEGORIES: ReadonlyArray<{
  value: ChallengeCategoryValue;
  label: string;
  emoji: string;
}> = [
  { value: "Core Business Foundations",        label: "Core Business Foundations",        emoji: "🏗️" },
  { value: "Product & Technology",             label: "Product & Technology",             emoji: "💡" },
  { value: "Growth & Go-To-Market",            label: "Growth & Go-To-Market",            emoji: "🚀" },
  { value: "Fundraising & Investor Readiness", label: "Fundraising & Investor Readiness", emoji: "💰" },
  { value: "Operations & Execution",           label: "Operations & Execution",           emoji: "⚙️" },
  { value: "Financial & Business Health",      label: "Financial & Business Health",      emoji: "📊" },
  { value: "Legal & Compliance",               label: "Legal & Compliance",               emoji: "⚖️" },
  { value: "Retention & Experience",           label: "Retention & Experience",           emoji: "🤝" },
  { value: "Other",                            label: "Other",                            emoji: "✏️" },
];

// ─── Startup Application Form Schema (FL-001) ───────────────────────────────

export const startupApplicationSchema = z
  .object({
    // Personal
    name: z.string().optional(),
    founderName: z.string().min(1, "Founder name (SPOC) is required"),
    email: z
      .string()
      .min(1, "This field is required")
      .email("Please enter a valid email address"),
    phone: z.string().optional(),
    spocLinkedin: z
      .string()
      .optional()
      .refine(
        (val) =>
          !val || val === "" || /^https?:\/\/(www\.)?linkedin\.com\/.+/.test(val),
        "Please enter a valid LinkedIn URL (must start with linkedin.com)"
      ),
    inviteCode: z.string().min(1, "Invite code is required"),

    // Startup details
    companyName: z.string().min(1, "This field is required"),
    stage: z.enum(
      [
        "Idea generation",
        "Validation / MVP / Prototype",
        "Product-market fit / Zero Revenue",
        "Early revenue / Seed stage",
        "Scaling / Growth (Series A/B)",
        "Maturity / Profitability (Series C & above)",
        "Pre-IPO / Exit",
      ],
      { message: "Please select a stage" }
    ),
    sector: z.enum(
      [
        "FinTech",
        "SaaS",
        "HealthTech",
        "EdTech",
        "CleanTech",
        "E-Commerce",
        "AI/ML",
        "BioTech",
        "Other",
      ],
      { message: "Please select a sector" }
    ),
    website: z
      .string()
      .optional()
      .refine(
        (val) => !val || val === "" || /^https?:\/\/.+/.test(val),
        "Please enter a valid URL (must start with http:// or https://)"
      ),

    // Startup description
    startupDescription: z
      .string()
      .min(1, "This field is required")
      .refine(
        (val) => val.trim().split(/\s+/).filter(Boolean).length >= 20,
        "Please write at least 20 words."
      )
      .refine(
        (val) => val.trim().split(/\s+/).filter(Boolean).length <= 80,
        "Maximum 80 words."
      ),

    // Challenge — category tile selection
    challengeCategory: z.enum(CHALLENGE_CATEGORY_VALUES, {
      message: "Please select your primary challenge area.",
    }),
    challengeOther: z.string().optional(),

    // Discovery
    discoverySource: z.enum(
      ["LinkedIn", "Twitter", "Referral", "Search", "Event", "Other"],
      { message: "Please select an option" }
    ),

    // T&C
    termsAccepted: z
      .boolean()
      .refine((val) => val === true, "You must accept the terms & conditions"),
  })
  .superRefine((data, ctx) => {
    // When "Other" is chosen, the free-text field is required
    if (data.challengeCategory === "Other") {
      if (!data.challengeOther || data.challengeOther.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Please describe your challenge.",
          path: ["challengeOther"],
        });
      }
      if (data.challengeOther && data.challengeOther.trim().length > 200) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Maximum 200 characters.",
          path: ["challengeOther"],
        });
      }
    }
  });

export type StartupApplicationFormValues = z.infer<
  typeof startupApplicationSchema
>;

export const requestInviteCodeSchema = z.object({
  name: z.string().min(1, "Full name is required"),
  founderName: z.string().min(1, "Founder name is required"),
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address"),
  spocLinkedin: z
    .string()
    .optional()
    .refine(
      (val) =>
        !val || val === "" || /^https?:\/\/(www\.)?linkedin\.com\/.+/.test(val),
      "Please enter a valid LinkedIn URL"
    ),
});

export type RequestInviteCodeFormValues = z.infer<typeof requestInviteCodeSchema>;


// ─── Investor Application Form Schema (FL-002) ──────────────────────────────

export const investorApplicationSchema = z.object({
  // Personal/Firm
  name: z.string().min(1, "This field is required"),
  email: z
    .string()
    .min(1, "This field is required")
    .email("Please enter a valid email address"),
  inviteCode: z.string().optional(),
  firmName: z.string().optional(),
  roleTitle: z.string().optional(),

  // Investment Profile
  investmentThesis: z
    .string()
    .min(200, "Minimum 200 characters")
    .max(1000, "Maximum 1000 characters"),
  stageFocus: z.array(z.string()).min(1, "Select at least one stage focus"),
  sectorFocus: z
    .array(z.string())
    .min(1, "Select at least one sector focus"),
  portfolioSize: z.enum([
    "first_time_0",
    "small_1_5",
    "medium_6_15",
    "large_16_50",
    "enterprise_50_plus",
  ]),
  checkSizeMin: z.number().positive().optional().nullable(),
  checkSizeMax: z.number().positive().optional().nullable(),
  geographicFocus: z.array(z.string()).optional(),

  // Discovery
  discoverySource: z.enum([
    "LinkedIn",
    "Twitter",
    "Referral",
    "Search",
    "Event",
    "Other",
  ]),

  // T&C
  termsAccepted: z
    .boolean()
    .refine((val) => val === true, "You must accept the terms & conditions"),
});

export type InvestorApplicationFormValues = z.infer<
  typeof investorApplicationSchema
>;

// ─── Stage & Sector Options ─────────────────────────────────────────────────

export const STARTUP_STAGES = [
  "Idea generation",
  "Validation / MVP / Prototype",
  "Product-market fit / Zero Revenue",
  "Early revenue / Seed stage",
  "Scaling / Growth (Series A/B)",
  "Maturity / Profitability (Series C & above)",
  "Pre-IPO / Exit",
] as const;

export const SECTORS = [
  "FinTech",
  "SaaS",
  "HealthTech",
  "EdTech",
  "CleanTech",
  "E-Commerce",
  "AI/ML",
  "BioTech",
  "Other",
] as const;

export const DISCOVERY_SOURCES = [
  "LinkedIn",
  "Twitter",
  "Referral",
  "Search",
  "Event",
  "Other",
] as const;

export const INVESTOR_STAGES = [
  "Pre-Seed",
  "Seed",
  "Series A",
  "Growth",
  "Late Stage",
] as const;

export const GEOGRAPHIC_FOCUSES = [
  "India",
  "Southeast Asia",
  "US",
  "Europe",
  "Global",
  "Other",
] as const;

export const PORTFOLIO_SIZE_OPTIONS = [
  { value: "first_time_0" as const, label: "First-time (0)" },
  { value: "small_1_5" as const, label: "1-5" },
  { value: "medium_6_15" as const, label: "6-15" },
  { value: "large_16_50" as const, label: "16-50" },
  { value: "enterprise_50_plus" as const, label: "50+" },
] as const;
